/**
 * Pure exercise-notes store logic (Phase 5).
 *
 * Single-line notes keyed by normalized exercise name so a note recalls across
 * different generated plans. Phase/day notes use reserved keys (global, simple).
 * Local-only this iteration — the adapter/composable handles localStorage; no
 * Firestore sync. `updatedAt` is stored so a later last-write-wins sync needs
 * no migration.
 */
import { normalizeExerciseKey } from '@/shared/utils/exerciseKey'

export interface ExerciseNote {
  text: string
  updatedAt: string // ISO-8601
  sourcePlanId?: string
}

/** localStorage `af_pt_exercise_notes` shape. */
export type NotesMap = Record<string, ExerciseNote>

export type NoteTarget =
  | { kind: 'exercise'; name: string }
  | { kind: 'warmup' }
  | { kind: 'cooldown' }
  | { kind: 'day' }

export interface WriteMeta {
  now?: string
  sourcePlanId?: string
}

/** Main-exercise notes → normalized name; phase/day → reserved global keys. */
export function noteKey(target: NoteTarget): string {
  return target.kind === 'exercise' ? normalizeExerciseKey(target.name) : `__${target.kind}__`
}

export function getNoteText(map: NotesMap, target: NoteTarget): string {
  return map[noteKey(target)]?.text ?? ''
}

/**
 * Returns a NEW map with the note written (or removed when text is blank).
 * Overwrite = most-recent-wins for a single local user.
 */
export function writeNote(map: NotesMap, target: NoteTarget, text: string, meta: WriteMeta = {}): NotesMap {
  const key = noteKey(target)
  const next: NotesMap = { ...map }
  const trimmed = text.trim()
  if (!trimmed) {
    delete next[key]
    return next
  }
  const note: ExerciseNote = { text: trimmed, updatedAt: meta.now ?? new Date().toISOString() }
  if (meta.sourcePlanId) note.sourcePlanId = meta.sourcePlanId
  next[key] = note
  return next
}
